import { useEffect, useRef } from 'react';
import { logger } from '@/lib/logger';

/**
 * MIGRATION_VERSION
 * Bump this constant whenever a breaking refactor ships and ALL clients
 * must reload to pick up new bundles (e.g. removed UI like the per-project
 * Globe domain wizard that has been replaced by the org-wide Settings panel).
 *
 * On first load after a bump:
 *  1. All caches are wiped.
 *  2. Any waiting service worker is asked to SKIP_WAITING.
 *  3. The page hard-reloads exactly once.
 *
 * Subsequent loads are no-ops until the constant changes again.
 */
export const MIGRATION_VERSION = '2026-04-30-sender-domains-v1';
const STORAGE_KEY = 'maestro-migration-version';

export function useMigrationVersion() {
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === MIGRATION_VERSION) return;

    (async () => {
      logger.info('[Migration] Version mismatch — forcing PWA update', {
        stored,
        target: MIGRATION_VERSION,
      });

      // 1. Persist new version FIRST so the post-reload run doesn't loop.
      localStorage.setItem(STORAGE_KEY, MIGRATION_VERSION);

      try {
        // 2. Wipe all caches (PWA precache + runtime caches).
        if ('caches' in window) {
          const keys = await caches.keys();
          await Promise.all(keys.map((k) => caches.delete(k)));
        }

        // 3. Activate any waiting service worker.
        if ('serviceWorker' in navigator) {
          const reg = await navigator.serviceWorker.getRegistration();
          if (reg) {
            try { await reg.update(); } catch { /* offline */ }
            if (reg.waiting) {
              reg.waiting.postMessage({ type: 'SKIP_WAITING' });
              // controllerchange listener in PWAContext will reload.
              return;
            }
          }
        }
      } catch (err) {
        logger.error('[Migration] Cleanup failed, hard-reloading anyway', err);
      }

      // 4. Fallback: hard reload.
      sessionStorage.setItem('pwa-just-updated', '1');
      window.location.reload();
    })();
  }, []);
}
